/* ============================================================
   SUPERDUPER HOBO — PDP skin behaviour (assets/sd-hobo-pdp.js)
   ------------------------------------------------------------
   Loaded by sections/template--product.liquid only when the
   section setting sd_pdp_skin = hobo (classic defer script, one
   init guard). The theme re-renders the buy box blocks on every
   size change (component-product-information.js replaces every
   [data-update-id]), so nothing below keeps element references:
   document-level delegation and fresh queries only.

   1. "No size preselected" (spec §6.5 / §6.7). Until a size is
      picked, payment-buttons renders the CTA disabled with
      [data-sd-hobo-needs-size] and "SELECT A SIZE". Here:
      - submit guard (capture): Enter, programmatic clicks or any
        script that re-enables the button cannot add the
        pre-filled first size;
      - the sitewide sticky bar mirrors `disabled` from the native
        button (sd-sticky-atc.js). A disabled button gets no click,
        so while no size is chosen its CTA is turned into
        aria-disabled and a click scrolls to the size grid and
        focuses the first pill instead of proxying an add;
      - the in-page CTA lets the click through to its row
        (pointer-events:none in sd-hobo-pdp.css): same nudge.
   2. Size pick: the CTA label, the hidden id and the sticky label
      follow at once ("ADD TO CART — SIZE 58"); the theme
      re-render then brings the same state from the server. The
      re-render replaces the pills: if focus fell to <body>, it
      goes back to the picked pill (same id) on 'variant:updated'.
   3. "ADDED TO CART ✓": after an add made by this form, when the
      theme emits eventBus 'update:cart:drawer' with the same
      variant (the drawer still opens as sitewide). Reset by the
      next size change (the button is re-rendered).
   4. Size guide: a capture click on the theme size guide link
      opens dialog#hb-size-guide (sections/sd-hobo-size-guide)
      through window.sdHobo.openDialog (assets/sd-hobo.js) instead
      of the theme drawer; no dialog on the page = theme behaviour.
   5. Desktop gallery (snippets/sd-hobo-media.liquid): one slide
      shown at a time — arrows ‹ ›, thumbnails, keyboard ← → (focus
      in the gallery), swipe on touch screens; wraps at both ends.
      The first slide keeps the real column `sizes` and its own
      desktop preload (sections/template--product.liquid); the next
      and previous slides are turned eager so a click shows a loaded
      file. Lightbox on the shown image (own dialog, not
      .sd-hobo-dialog) with its own arrows, ← → and swipe; a click
      elsewhere / Esc / close:all:drawers closes it and the stage
      stays on the last image viewed. Scroll lock: body.js-drawer-open
      is removed on close only if this code added it and no theme
      drawer is open meanwhile.
   6. Mobile (< 768px): arrows ‹ › added on the theme slider
      (#productMediaMobile, the 1A one with the "1 / N" counter),
      driving its Swiper (wraps at both ends). Re-added after a
      re-render (variant:updated, theme editor section load).
   7. Pre-sale strip on the sticky bar (ships line, per size).
   8. Waitlist form on a sold-out size (16/10/2026).
   ============================================================ */
(function () {
  if (window.__sdHoboPdpInit) return;
  window.__sdHoboPdpInit = true;

  var ROOT = '.sd-pdp-hobo';
  var reduceMq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function root() { return document.querySelector(ROOT); }
  function nativeBtn() { return document.querySelector(ROOT + ' product-form button[name="add"]'); }
  function needsSize() {
    var b = nativeBtn();
    return !!(b && b.hasAttribute('data-sd-hobo-needs-size'));
  }
  function stickyBtn() { return document.querySelector('[data-sd-sticky-add]'); }
  function stickyBar() { return document.querySelector('[data-sd-sticky]'); }
  function stickyLabel() { return document.querySelector('[data-sd-sticky-add] [data-sd-sticky-label]'); }
  function sizeHost() {
    var r = root();
    if (!r) return null;
    return r.querySelector('[data-sd-size-pills]:not(.sd-size-pills--text)') || r.querySelector('[data-sd-size-pills]');
  }
  function focusNoScroll(el) {
    if (!el || typeof el.focus !== 'function') return;
    try { el.focus({ preventScroll: true }); } catch (err) { el.focus(); }
  }

  /* ── 1. Go to the sizes (sticky CTA, in-page CTA, blocked submit) ── */
  var flashTimer = null;
  function focusSizes() {
    var host = sizeHost();
    if (!host) return;
    var input = host.querySelector('.sd-size-pill.available input[type="radio"]') || host.querySelector('input[type="radio"]');
    var smooth = !(reduceMq && reduceMq.matches);
    try { host.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'center' }); } catch (err) { host.scrollIntoView(); }
    focusNoScroll(input);
    host.classList.add('hb-size-flash');
    clearTimeout(flashTimer);
    flashTimer = setTimeout(function () {
      var h = sizeHost();
      if (h) h.classList.remove('hb-size-flash');
    }, 1600);
  }

  /* sticky bar: keep its CTA clickable (aria-disabled) while no size is chosen */
  function syncSticky() {
    var s = stickyBtn();
    if (!s) return;
    var bar = stickyBar();
    var ns = !!root() && needsSize();
    if (bar) {
      if (ns) bar.setAttribute('data-sd-hobo-needs-size', '');
      else bar.removeAttribute('data-sd-hobo-needs-size');
    }
    if (ns) {
      if (s.disabled) s.disabled = false;
      if (s.getAttribute('aria-disabled') !== 'true') s.setAttribute('aria-disabled', 'true');
      if (!s.hasAttribute('data-sd-hobo-needs-size')) s.setAttribute('data-sd-hobo-needs-size', '');
    } else if (s.hasAttribute('data-sd-hobo-needs-size')) {
      s.removeAttribute('data-sd-hobo-needs-size');
      s.removeAttribute('aria-disabled');
    }
  }
  /* after a re-render: copy the fresh native label + state (the bar's own copy runs on a
     fixed 350 ms timer that a slow section fetch can beat) */
  function mirrorSticky() {
    var s = stickyBtn();
    var b = nativeBtn();
    if (!s || !b) return;
    var span = b.querySelector('[data-add-to-cart-text]');
    var text = ((span || b).textContent || '').replace(/\s+/g, ' ').trim();
    var sl = stickyLabel();
    if (sl && text) sl.textContent = text;
    if (!b.hasAttribute('data-sd-hobo-needs-size')) s.disabled = b.disabled;
    syncSticky();
  }
  function observeSticky() {
    var s = stickyBtn();
    if (!s || s.__sdHoboObserved || !window.MutationObserver) return;
    s.__sdHoboObserved = true;
    /* sd-sticky-atc.js copies `disabled` from the native button at init and 350 ms after
       every variant change: flip it back as soon as it lands while no size is chosen */
    new MutationObserver(syncSticky).observe(s, { attributes: true, attributeFilter: ['disabled'] });
  }

  /* blocked submit (capture: runs before <product-form>'s own submit listener) + pending add */
  var pending = null;
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.closest || !form.closest(ROOT)) return;
    var btn = form.querySelector('button[name="add"]');
    if (!btn) return;
    if (btn.hasAttribute('data-sd-hobo-needs-size')) {
      e.preventDefault();
      e.stopImmediatePropagation();
      focusSizes();
      return;
    }
    var idInput = form.querySelector('input[name="id"]');
    pending = idInput && idInput.value ? { id: String(idInput.value), at: Date.now() } : null;
  }, true);

  /* ── 4/5. Dialog helpers (size guide fallback + lightbox) ── */
  var ownDialogs = [];
  function track(dlg) { if (ownDialogs.indexOf(dlg) === -1) ownDialogs.push(dlg); }
  function anyHoboDialogOpen() { return !!document.querySelector('dialog.sd-hobo-dialog[open], dialog[data-hb-lightbox][open]'); }
  /* scroll lock shared with the theme drawers (same rule as assets/sd-hobo.js): add the class only
     if nobody holds it, remove it only if this code added it and no theme drawer opened meanwhile
     (e.g. close:all:drawers + cart drawer while the lightbox was open) */
  var lockOwned = false;
  var THEME_DRAWERS_OPEN = 'cart-drawer[aria-hidden="false"], .global-drawer[aria-hidden="false"], size-guide-drawer[aria-hidden="false"], offers-drawer[aria-hidden="false"], store-availability-drawer[aria-hidden="false"]';
  function themeDrawerOpen() {
    var b = document.body;
    return b.classList.contains('js-drawer-open-left') || b.classList.contains('js-drawer-open-filter') ||
      !!document.querySelector(THEME_DRAWERS_OPEN);
  }
  function takeLock() {
    var b = document.body;
    if (b.classList.contains('js-drawer-open')) return;
    b.classList.add('js-drawer-open');
    lockOwned = true;
  }
  function releaseLock() {
    if (anyHoboDialogOpen()) return;
    if (lockOwned && !themeDrawerOpen()) document.body.classList.remove('js-drawer-open');
    lockOwned = false;
  }
  /* the native 'close' event (Escape, close()) is queued: it also covers Escape, while
     closeDialog() releases the scroll lock at once (a quick close/reopen can skip an event) */
  function afterClose(dlg, opener) {
    var onClose = function () {
      dlg.removeEventListener('close', onClose);
      releaseLock();
      if (opener && document.contains(opener)) focusNoScroll(opener);
    };
    dlg.addEventListener('close', onClose);
  }
  function showDialog(dlg, opener) {
    if (dlg.open) return;
    try { dlg.showModal(); } catch (err) { dlg.setAttribute('open', ''); }
    takeLock();
    track(dlg);
    afterClose(dlg, opener);
  }
  function closeDialog(dlg) {
    if (!dlg || !dlg.open) return;
    if (typeof dlg.close === 'function') dlg.close();
    else dlg.removeAttribute('open');
    releaseLock();
    if (typeof dlg.__hbRefocus === 'function') dlg.__hbRefocus();
  }
  function closeOwnDialogs() { ownDialogs.forEach(closeDialog); }

  /* size guide: the shared opener of assets/sd-hobo.js (window.sdHobo.openDialog: closes the
     theme drawers, scroll lock, focus back to the link, Escape/backdrop/close button); the local
     fallback above only runs if that module failed to load */
  function openSizeGuide(dlg, opener) {
    var api = window.sdHobo || window.SDHobo;
    if (api && typeof api.openDialog === 'function') {
      api.openDialog(dlg, opener);
      return;
    }
    showDialog(dlg, opener);
  }

  /* ── 5. Desktop gallery: slides, arrows, thumbnails ── */
  function galleryOf(el) { return el && el.closest ? el.closest('[data-hb-media]') : null; }
  function slidesOf(media) { return media ? media.querySelectorAll('[data-hb-slide]') : []; }
  function wrap(i, n) { return ((i % n) + n) % n; }
  function activeIndex(media) {
    var s = slidesOf(media);
    for (var i = 0; i < s.length; i++) if (s[i].classList.contains('is-active')) return i;
    return 0;
  }
  /* lazy + display:none never loads: turn a neighbour eager before it is needed */
  function warm(slide) {
    var img = slide && slide.querySelector('img');
    if (img && img.getAttribute('loading') === 'lazy') img.setAttribute('loading', 'eager');
  }
  function showSlide(media, index) {
    var s = slidesOf(media);
    var n = s.length;
    if (n < 2) return 0;
    index = wrap(index, n);
    for (var i = 0; i < n; i++) {
      var on = i === index;
      s[i].classList.toggle('is-active', on);
      /* only the shown slide is reachable with Tab */
      if (on) s[i].removeAttribute('tabindex');
      else s[i].setAttribute('tabindex', '-1');
    }
    var thumbs = media.querySelectorAll('[data-hb-media-thumb]');
    for (var j = 0; j < thumbs.length; j++) {
      var cur = parseInt(thumbs[j].getAttribute('data-hb-media-thumb'), 10) === index;
      thumbs[j].classList.toggle('is-active', cur);
      thumbs[j].setAttribute('aria-current', cur ? 'true' : 'false');
    }
    var counter = media.querySelector('[data-hb-media-counter]');
    if (counter) counter.textContent = (index + 1) + ' / ' + n;
    warm(s[wrap(index + 1, n)]);
    warm(s[wrap(index - 1, n)]);
    return index;
  }
  function warmFirst() {
    document.querySelectorAll(ROOT + ' [data-hb-media]').forEach(function (media) {
      var s = slidesOf(media);
      if (s.length > 1) { warm(s[1]); warm(s[s.length - 1]); }
    });
  }

  /* lightbox: the shown slide's file, own arrows and counter; the stage follows */
  function lightboxShow(lb, media, index) {
    var s = slidesOf(media);
    var n = s.length;
    if (!n) return;
    index = wrap(index, n);
    lb.__hbIndex = index;
    var link = s[index];
    var img = lb.querySelector('[data-hb-lightbox-img]');
    var thumb = link.querySelector('img');
    var full = link.getAttribute('href');
    if (img) {
      img.alt = thumb ? (thumb.getAttribute('alt') || '') : '';
      /* what is already decoded first, then the 2048px file (only if still the one asked for) */
      lb.__sdHoboFull = full;
      var shown = thumb && thumb.currentSrc;
      if (shown) img.src = shown;
      else if (full) img.src = full;
      if (full && shown) {
        var pre = new Image();
        pre.onload = function () { if (lb.open && lb.__sdHoboFull === full) img.src = full; };
        pre.src = full;
      }
    }
    var c = lb.querySelector('[data-hb-lightbox-counter]');
    if (c) c.textContent = n > 1 ? (index + 1) + ' / ' + n : '';
    if (n > 1) {
      showSlide(media, index);
      [index + 1, index - 1].forEach(function (k) {
        var h = s[wrap(k, n)].getAttribute('href');
        if (h) { var p = new Image(); p.src = h; }
      });
    }
  }
  function navLightbox(lb, dir) {
    var media = galleryOf(lb);
    if (!media) return;
    lightboxShow(lb, media, (lb.__hbIndex || 0) + dir);
  }
  function openLightbox(lb, link) {
    var media = galleryOf(link);
    var index = parseInt(link.getAttribute('data-hb-slide'), 10) || 0;
    lightboxShow(lb, media, index);
    showDialog(lb, null);
    /* focus back to the slide shown after closing (the one opened may be hidden by then): run by
       closeDialog() at once, or by the queued 'close' event for Escape */
    var done = false;
    var refocus = function () {
      if (done) return;
      done = true;
      lb.removeEventListener('close', refocus);
      lb.__hbRefocus = null;
      var s = slidesOf(media)[activeIndex(media)];
      if (s && document.contains(s)) focusNoScroll(s);
    };
    lb.__hbRefocus = refocus;
    lb.addEventListener('close', refocus);
  }

  /* ── 6. Mobile: arrows on the theme slider ── */
  var SVG_PREV = '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="20" height="20"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square"/></svg>';
  var SVG_NEXT = '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="20" height="20"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square"/></svg>';
  function makeArrow(dir) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'hb-swiper-arrow hb-swiper-arrow--' + dir;
    b.setAttribute('data-hb-swiper-' + dir, '');
    b.setAttribute('aria-label', dir === 'prev' ? 'Previous image' : 'Next image');
    b.innerHTML = dir === 'prev' ? SVG_PREV : SVG_NEXT;
    return b;
  }
  /* true = done (or nothing to do), false = the theme Swiper is not ready yet */
  function bindMobileArrows() {
    var r = root();
    if (!r) return true;
    var pm = r.querySelector('#productMediaMobile');
    var main = pm && pm.querySelector('[data-slider-main]');
    if (!main) return true;
    var sw = main.swiper;
    if (!sw) return false;
    if (main.querySelector('[data-hb-swiper-prev]')) return true;
    var total = parseInt(pm.getAttribute('data-media-count'), 10) || (sw.slides ? sw.slides.length : 0);
    if (total < 2) return true;
    main.appendChild(makeArrow('prev'));
    main.appendChild(makeArrow('next'));
    return true;
  }
  var mobileTries = 0;
  function initMobileArrows() {
    if (bindMobileArrows()) return;
    if (++mobileTries > 40) return;
    setTimeout(initMobileArrows, 150);
  }
  function stepSwiper(btn, dir) {
    var main = btn.closest('[data-slider-main]');
    var sw = main && main.swiper;
    if (!sw) return;
    var n = sw.slides ? sw.slides.length : 0;
    if (dir < 0) {
      if (sw.isBeginning && n > 1 && !sw.params.loop) sw.slideTo(n - 1);
      else sw.slidePrev();
    } else if (sw.isEnd && !sw.params.loop) {
      sw.slideTo(0);
    } else {
      sw.slideNext();
    }
  }

  /* keyboard: ← → in the open lightbox, or with focus in the desktop gallery */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
    var dir = e.key === 'ArrowLeft' ? -1 : 1;
    var lb = document.querySelector(ROOT + ' dialog[data-hb-lightbox][open]');
    if (lb) {
      e.preventDefault();
      navLightbox(lb, dir);
      return;
    }
    var t = e.target;
    var media = t && t.closest ? t.closest(ROOT + ' [data-hb-media]') : null;
    if (!media || slidesOf(media).length < 2) return;
    e.preventDefault();
    var idx = showSlide(media, activeIndex(media) + dir);
    if (t.hasAttribute('data-hb-slide')) focusNoScroll(slidesOf(media)[idx]);
  });

  /* swipe on touch screens (tablets from 768px see the desktop gallery) */
  var touch = null;
  var suppressClickUntil = 0;
  document.addEventListener('touchstart', function (e) {
    var t = e.target;
    touch = null;
    if (!t || !t.closest || !e.touches || e.touches.length !== 1 || t.closest('button')) return;
    var host = t.closest(ROOT + ' [data-hb-stage]') || t.closest(ROOT + ' dialog[data-hb-lightbox][open]');
    if (!host) return;
    touch = { host: host, x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, { passive: true });
  document.addEventListener('touchend', function (e) {
    if (!touch) return;
    var start = touch;
    touch = null;
    var p = e.changedTouches && e.changedTouches[0];
    if (!p) return;
    var dx = p.clientX - start.x;
    var dy = p.clientY - start.y;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
    suppressClickUntil = Date.now() + 500;
    var dir = dx < 0 ? 1 : -1;
    if (start.host.tagName === 'DIALOG') navLightbox(start.host, dir);
    else {
      var media = galleryOf(start.host);
      if (media) showSlide(media, activeIndex(media) + dir);
    }
  }, { passive: true });

  /* ── one capture click listener for every delegated case ── */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    /* sticky CTA while no size is chosen: go to the sizes instead of a silent no-op */
    if (t.closest('[data-sd-sticky-add]')) {
      if (!root() || !needsSize()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      focusSizes();
      return;
    }

    /* open lightbox: its arrows navigate, any other click closes it (not right after a swipe) */
    var lbOpen = t.closest('dialog[data-hb-lightbox]');
    if (lbOpen) {
      if (lbOpen.open) {
        e.preventDefault();
        var lbNav = t.closest('[data-hb-lightbox-prev], [data-hb-lightbox-next]');
        if (lbNav) {
          e.stopPropagation();
          navLightbox(lbOpen, lbNav.hasAttribute('data-hb-lightbox-prev') ? -1 : 1);
        } else if (Date.now() >= suppressClickUntil) {
          closeDialog(lbOpen);
        }
      }
      return;
    }

    /* size guide fallback dialog (opened here): close button / backdrop */
    var own = t.closest('dialog.sd-hobo-dialog');
    if (own && ownDialogs.indexOf(own) !== -1 && own.open) {
      if (t === own || t.closest('[data-hb-dialog-close]')) closeDialog(own);
      return;
    }

    if (!t.closest(ROOT)) return;

    /* in-page CTA while no size is chosen (the disabled button lets the click through) */
    var row = t.closest('.paymentButtonsWrapper');
    if (row && t === row) {
      var b = row.querySelector('button[name="add"][data-sd-hobo-needs-size]');
      if (b) {
        var r = b.getBoundingClientRect();
        if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) {
          e.preventDefault();
          focusSizes();
        }
      }
      return;
    }

    /* theme size guide link → HOBO size guide dialog, when the section is on the page */
    var guide = t.closest('[data-size-guide-link], size-guide-link a');
    if (guide) {
      var sg = document.getElementById('hb-size-guide');
      if (!sg || typeof sg.showModal !== 'function') return; /* theme drawer, as before */
      e.preventDefault();
      e.stopImmediatePropagation();
      openSizeGuide(sg, guide);
      return;
    }

    /* mobile slider arrows (theme Swiper): stop here so no theme click handler sees them */
    var swArrow = t.closest('[data-hb-swiper-prev], [data-hb-swiper-next]');
    if (swArrow) {
      e.preventDefault();
      e.stopPropagation();
      stepSwiper(swArrow, swArrow.hasAttribute('data-hb-swiper-prev') ? -1 : 1);
      return;
    }

    /* desktop gallery: arrows and thumbnails */
    var arrow = t.closest('[data-hb-media-prev], [data-hb-media-next]');
    if (arrow) {
      var am = galleryOf(arrow);
      e.preventDefault();
      e.stopPropagation();
      if (am) showSlide(am, activeIndex(am) + (arrow.hasAttribute('data-hb-media-prev') ? -1 : 1));
      return;
    }
    var thumbBtn = t.closest('[data-hb-media-thumb]');
    if (thumbBtn) {
      var tm = galleryOf(thumbBtn);
      e.preventDefault();
      if (tm) showSlide(tm, parseInt(thumbBtn.getAttribute('data-hb-media-thumb'), 10) || 0);
      return;
    }

    /* desktop gallery → lightbox (not right after a swipe) */
    var zoom = t.closest('[data-hb-zoom]');
    if (zoom) {
      var media = zoom.closest('[data-hb-media]');
      var lb = media && media.querySelector('dialog[data-hb-lightbox]');
      if (!lb || typeof lb.showModal !== 'function') return; /* plain link to the 2048px file */
      e.preventDefault();
      if (Date.now() < suppressClickUntil) return;
      openLightbox(lb, zoom);
    }
  }, true);

  /* ── 2. Size pick: label, id and sticky label at once ── */
  function readVariant(host, inputId) {
    if (!host || !inputId) return null;
    var el = host.querySelector('script[type="application/json"][data-resource="' + inputId + '"]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (err) { return null; }
  }
  function clearAdded() {
    var b = nativeBtn();
    if (b) b.removeAttribute('data-sd-hobo-added');
  }
  var lastPick = null;
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || !input.matches || !input.matches(ROOT + ' [data-sd-size-pills] input[type="radio"]')) return;
    clearAdded();
    pending = null;
    lastPick = input.id ? { id: input.id, at: Date.now() } : null;
    var host = input.closest('[data-sd-size-pills]');
    /* another option than the size: the re-render brings the label */
    if (!host || host.classList.contains('sd-size-pills--text')) return;
    var btn = nativeBtn();
    var variant = readVariant(host, input.id);
    if (!btn || !variant || !variant.available) return; /* sold out: the re-render says so */

    var form = btn.closest('form');
    var idInput = form && form.querySelector('input[name="id"]');
    if (idInput && variant.id) {
      idInput.value = variant.id;
      idInput.disabled = false;
    }
    btn.removeAttribute('data-sd-hobo-needs-size');
    btn.removeAttribute('aria-disabled');
    btn.disabled = false;

    var r = root();
    var add = r ? (r.getAttribute('data-hb-cta-add') || '') : '';
    var label = (add + ' ' + input.value).replace(/\s+/g, ' ').trim();
    var span = btn.querySelector('[data-add-to-cart-text]');
    if (span && add) span.textContent = label;
    syncSticky();
    var sl = stickyLabel();
    if (sl && add) sl.textContent = label;
  });

  /* ── 3. "ADDED TO CART ✓" ── */
  function onCartUpdate(payload) {
    if (!pending) return;
    if (Date.now() - pending.at > 20000) { pending = null; return; }
    /* the /cart/add response of a single item is the line item (top-level variant_id);
       cart changes from the drawer send the whole cart and are ignored */
    var vid = payload && payload.variant_id != null ? String(payload.variant_id) : '';
    if (!vid || vid !== pending.id) return;
    pending = null;
    var r = root();
    var label = r ? r.getAttribute('data-hb-cta-added') : '';
    var btn = nativeBtn();
    if (!label || !btn) return;
    var span = btn.querySelector('[data-add-to-cart-text]');
    if (span) span.textContent = label;
    btn.setAttribute('data-sd-hobo-added', '');
    var sl = stickyLabel();
    if (sl) sl.textContent = label;
  }
  function onCartAdded(data) {
    var r = root();
    if (data && data.sectionId && r && r.getAttribute('data-wetheme-section-id') !== String(data.sectionId)) return;
    /* fires after update:cart:drawer, also on errors: drop a pending add that got no success */
    setTimeout(function () { pending = null; }, 0);
  }

  /* after the re-render: the pills are new nodes and the theme only refocuses option-* radios,
     so focus falls to <body>; give it back to the picked pill (stable id). Runs after the theme's
     own refocus (macrotask after the synchronous emit). */
  function refocusPick() {
    var pick = lastPick;
    if (!pick || Date.now() - pick.at > 10000) return;
    setTimeout(function () {
      var a = document.activeElement;
      if (a && a !== document.body && a !== document.documentElement) return;
      var el = document.getElementById(pick.id);
      if (el && el.closest(ROOT)) focusNoScroll(el);
      lastPick = null;
    }, 0);
  }

  /* ── eventBus (deferred in <head>: normally ready, polled just in case) ── */
  function withBus(fn) {
    if (window.eventBus && typeof window.eventBus.on === 'function') { fn(window.eventBus); return; }
    var tries = 0;
    var timer = setInterval(function () {
      if (window.eventBus && typeof window.eventBus.on === 'function') {
        clearInterval(timer);
        fn(window.eventBus);
      } else if (++tries > 100) {
        clearInterval(timer);
      }
    }, 50);
  }

  /* ── 7. Pre-sale: the line under the buttons ("SHIPS FROM 28 OCTOBER", snippets/sd-hobo-ships.liquid)
        becomes a strip on top of the sitewide sticky bar; no line (after the ship date) = no strip ── */
  function bindStickyShips() {
    var bar = stickyBar();
    var r = root();
    if (!bar || !r) return;
    var line = r.querySelector('[data-hb-ships]');
    var strip = bar.querySelector('.hb-sticky-ships');
    var text = line ? (line.textContent || '').replace(/\s+/g, ' ').trim() : '';
    if (!text) {
      if (strip) strip.parentNode.removeChild(strip);
      return;
    }
    if (!strip) {
      strip = document.createElement('p');
      strip.className = 'hb-sticky-ships';
      bar.insertBefore(strip, bar.firstChild);
    }
    strip.textContent = text;
  }

  /* ── 8. Waitlist on a sold-out size (snippets/sd-hobo-waitlist.liquid, 16/10/2026).
        The post itself is Shopify's: its captcha (inline "captcha-bootstrap") protects every customer
        form; once the form is bound (focus on the field) it cancels the submit, gets a token and posts
        natively. Here, once per signup, at submit time:
        - the Klaviyo event "HOBO Waitlist Joined" (public key) and, with a list ID, the Klaviyo list
          (client subscriptions API), both keepalive so they survive the page change;
        - a note in sessionStorage, for the confirmation after the reload when the page has no ?variant=
          (otherwise the snippet prints it server-side: form.posted_successfully?);
        - a submit the captcha swallowed before binding (no focus ever reached the form, e.g. autofill):
          bind it with a focusin, wait for it, submit again; no binding within 3 s = error line.
        A store without the captcha: nothing is cancelled and the native post goes ahead. ── */
  var WAIT_KEY = 'sdHoboWait';

  function waitKlaviyoEvent(company, email, props) {
    if (!company || !window.fetch) return;
    var body = { data: { type: 'event', attributes: {
      properties: props,
      metric: { data: { type: 'metric', attributes: { name: 'HOBO Waitlist Joined' } } },
      profile: { data: { type: 'profile', attributes: { email: email } } }
    } } };
    fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(company), {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', 'revision': '2025-07-15' },
      body: JSON.stringify(body)
    }).catch(function () {});
  }

  /* list: the documented client subscriptions API (public key). The old embed endpoint
     (manage.kmail-lists.com) answered "success" even for an invalid address and did not add
     anyone to the list (16/10/2026). A double opt-in list adds the profile only after the
     confirmation email is clicked (Klaviyo > list > Settings). */
  function waitKlaviyoList(company, list, email, props) {
    if (!company || !list || !window.fetch) return;
    var body = { data: {
      type: 'subscription',
      attributes: {
        custom_source: 'HOBO waitlist (PDP)',
        profile: { data: { type: 'profile', attributes: {
          email: email,
          properties: props,
          subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
        } } }
      },
      relationships: { list: { data: { type: 'list', id: list } } }
    } };
    fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(company), {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', 'revision': '2025-07-15' },
      body: JSON.stringify(body)
    }).catch(function () {});
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.id !== 'hb-wait-form') return;
    var box = form.closest('[data-hb-wait]');
    var emailEl = form.querySelector('input[type="email"]');
    var email = emailEl ? emailEl.value.trim() : '';
    if (!box || !email) return;

    var btn = form.querySelector('[data-hb-wait-btn]');
    var err = form.querySelector('[data-hb-wait-err]');
    if (err) err.hidden = true;

    if (form.getAttribute('data-hb-sent') !== '1') {
      form.setAttribute('data-hb-sent', '1');
      var model = box.getAttribute('data-model') || '';
      var size = box.getAttribute('data-size') || '';
      var props = {
        hobo_wait_model: model,
        hobo_wait_size: size,
        hobo_wait_product: box.getAttribute('data-product') || '',
        hobo_wait_source: 'pdp'
      };
      if (model) props['hobo_wait_' + model.replace(/[^a-z0-9]/gi, '_').toLowerCase()] = size;
      try {
        sessionStorage.setItem(WAIT_KEY, JSON.stringify({ t: Date.now(), ok: box.getAttribute('data-ok') || '', model: model, size: size }));
      } catch (x) {}
      waitKlaviyoEvent((box.getAttribute('data-klaviyo-company') || '').trim(), email, props);
      waitKlaviyoList((box.getAttribute('data-klaviyo-company') || '').trim(), (box.getAttribute('data-klaviyo-list') || '').trim(), email, props);
    }

    if (btn && !btn.hasAttribute('data-label')) btn.setAttribute('data-label', btn.textContent);
    function restore() {
      if (btn) { btn.disabled = false; btn.textContent = btn.getAttribute('data-label') || btn.textContent; }
    }
    if (btn) {
      btn.textContent = 'Sending…';
      /* an interactive challenge closed without solving it: give the button back */
      setTimeout(restore, 12000);
    }

    /* bound (or no captcha at all, nothing cancelled): Shopify posts natively */
    if (form.hasAttribute('data-hcaptcha-bound') || !e.defaultPrevented) return;

    /* the captcha cancelled a submit before binding: bind it, then submit again */
    if (btn) btn.disabled = true;
    if (emailEl) emailEl.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));
    var tries = 0;
    var timer = setInterval(function () {
      if (form.hasAttribute('data-hcaptcha-bound')) {
        clearInterval(timer);
        if (btn) btn.disabled = false;
        if (form.requestSubmit) form.requestSubmit(); else form.submit();
      } else if (++tries > 30) {
        clearInterval(timer);
        restore();
        if (err) err.hidden = false;
      }
    }, 100);
  });

  /* back from the post (?customer_posted=true): tracking, and the confirmation when the page was
     reloaded without the sold-out size (no server-side one in the buy box) */
  function waitReturn() {
    var raw = null;
    try { raw = sessionStorage.getItem(WAIT_KEY); } catch (e) { return; }
    if (raw === null) return;
    if (location.search.indexOf('customer_posted=true') === -1) return;
    try { sessionStorage.removeItem(WAIT_KEY); } catch (e) {}
    var note = {};
    try { note = JSON.parse(raw) || {}; } catch (e) {}
    if (!note.t || Date.now() - note.t > 15 * 60 * 1000) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'hobo_waitlist_signup', hobo_model: note.model || '', hobo_size: note.size || '' });
    var slot = document.querySelector(ROOT + ' [data-hb-wait-slot]');
    if (!slot) return;
    var shown = slot.querySelector('[data-hb-wait-ok-server]');
    if (!shown) {
      shown = document.createElement('p');
      shown.className = 'hb-wait__ok hb-wait__ok--return';
      shown.setAttribute('role', 'status');
      shown.tabIndex = -1;
      shown.textContent = note.ok || "You're on the list. We'll email you when it's available.";
      slot.appendChild(shown);
    }
    shown.scrollIntoView({ block: 'center' });
    try { shown.focus({ preventScroll: true }); } catch (e) {}
  }

  function init() {
    if (!root()) return;
    observeSticky();
    syncSticky();
    bindStickyShips();
    mobileTries = 0;
    initMobileArrows();
    waitReturn();
  }

  withBus(function (bus) {
    bus.on('update:cart:drawer', onCartUpdate);
    bus.on('cart:added', onCartAdded);
    bus.on('variant:change', function () {
      clearAdded();
      /* after sd-sticky-atc.js mirrors the native button (+350 ms) */
      setTimeout(function () { if (root()) syncSticky(); }, 400);
    });
    bus.on('variant:updated', function () {
      if (!root()) return;
      mirrorSticky();
      /* 16/10/2026: the ships line is per size now (none on a sold-out size): the sticky strip follows */
      bindStickyShips();
      refocusPick();
      mobileTries = 0;
      initMobileArrows();
    });
    bus.on('close:all:drawers', closeOwnDialogs);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  window.addEventListener('load', function () {
    init();
    /* after the page (and the LCP image) has loaded: get slide 2 and the last one ready */
    setTimeout(warmFirst, 300);
  });

  /* theme editor: the section is re-rendered in place */
  document.addEventListener('shopify:section:load', function () {
    setTimeout(init, 0);
  });
})();
